import React, { useState, useEffect } from 'react';
import { useAuth } from '../../utils/AuthContext';
import Header from "../../layouts/Header";
import { PremiumFeatureLock } from '../../components/premium/PremiumFeatureLock';

import { Card, Row, Col, Alert, Button, Form, Badge } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { getDataOnRealtimeDb } from '../../utils/firebase';
// import { getForecast } from '../../services/aiService';
import toast, { Toaster } from 'react-hot-toast';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export default function ExpenseForecast() {
    const { currentUser, subscription } = useAuth();
    const isPremium = subscription === 'premium';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [forecastData, setForecastData] = useState(null);
    const [historicalData, setHistoricalData] = useState(null);
    const [insights, setInsights] = useState([]);
    const [selectedTimeframe, setSelectedTimeframe] = useState('6months');
    const [confidenceLevel, setConfidenceLevel] = useState('medium');

    useEffect(() => {
        if (isPremium) {
            fetchData();
        }
    }, [currentUser, isPremium, selectedTimeframe, confidenceLevel]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const transactionsData = await getDataOnRealtimeDb(`users/${currentUser.uid}/transactions`);
            
            if (!transactionsData || transactionsData.length === 0) {
                throw new Error('No historical transaction data available');
            }

            const filteredData = transactionsData.filter(t => t.type === 'Expense');
            setHistoricalData(filteredData);

            if (filteredData.length === 0) {
                throw new Error('No historical expense data available');
            }

            const confidenceLevelMap = {
                'conservative': 0.9,
                'medium': 0.8,
                'aggressive': 0.7
            };

            // const { forecast, insights } = await getForecast(
            //     filteredData,
            //     selectedTimeframe,
            //     confidenceLevelMap[confidenceLevel]
            // );

            // setForecastData(forecast);
            // setInsights(insights);

        } catch (error) {
            console.error('Error fetching forecast data:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const getChartData = () => {
        if (!forecastData || !historicalData) return null;

        const dates = forecastData.dates;
        const historical = historicalData.map(t => ({
            date: new Date(t.date).toISOString().split('T')[0],
            amount: t.amount
        }));

        return {
            labels: dates,
            datasets: [
                {
                    label: 'Historical Expenses',
                    data: dates.map(date => {
                        const match = historical.find(h => h.date === date);
                        return match ? match.amount : null;
                    }),
                    borderColor: 'rgb(53, 162, 235)',
                    backgroundColor: 'rgba(53, 162, 235, 0.5)',
                    pointStyle: 'circle',
                    pointRadius: 5,
                    pointHoverRadius: 8
                },
                {
                    label: 'Forecasted Expenses',
                    data: forecastData.predicted,
                    borderColor: 'rgb(255, 99, 132)',
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                    borderDash: [5, 5],
                    pointStyle: 'circle',
                    pointRadius: 5,
                    pointHoverRadius: 8
                },
                {
                    label: 'Upper Bound',
                    data: forecastData.upper_bound,
                    borderColor: 'rgba(255, 99, 132, 0.2)',
                    backgroundColor: 'rgba(255, 99, 132, 0.1)',
                    borderDash: [2, 2],
                    pointRadius: 0,
                    fill: '+1'
                },
                {
                    label: 'Lower Bound',
                    data: forecastData.lower_bound,
                    borderColor: 'rgba(255, 99, 132, 0.2)',
                    backgroundColor: 'rgba(255, 99, 132, 0.1)',
                    borderDash: [2, 2],
                    pointRadius: 0,
                    fill: false
                }
            ]
        };
    };

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Expense Forecast with Confidence Intervals'
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Amount ($)'
                }
            }
        }
    };

    const renderInsightsCard = () => {
        if (!insights.length) return null;

        return (
            <Card className="mb-4">
                <Card.Header>
                    <Card.Title as="h6">
                        <i className="ri-lightbulb-flash-line me-2"></i>
                        AI-Generated Expense Insights
                    </Card.Title>
                </Card.Header>
                <Card.Body>
                    {insights.map((insight, index) => (
                        <div key={index} className="d-flex align-items-center mb-3">
                            <Badge bg={insight.severity} className="me-2">
                                {insight.type === 'trend' ? 'Trend Analysis' : 
                                 insight.type === 'seasonal' ? 'Seasonal Pattern' : 'Alert'}
                            </Badge>
                            <span>{insight.message}</span>
                        </div>
                    ))}
                </Card.Body>
            </Card>
        );
    };

    if (!isPremium) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="main-title mb-0">Expense Forecast</h4>
                    </div>

                    <PremiumFeatureLock 
                        title="AI-Powered Expense Forecast"
                        description="Get accurate expense predictions and insights using our advanced AI algorithms. Upgrade to premium to access this feature."
                    />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <Toaster position="top-right" />
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h4 className="main-title mb-0">Expense Forecast</h4>
                </div>

                <Alert variant="info" className="mb-4">
                    <Alert.Heading>
                        <i className="ri-information-line me-2"></i>
                        About AI Expense Forecasting
                    </Alert.Heading>
                    <p className="mb-2">
                        Our AI-powered expense forecasting uses machine learning algorithms to analyze your historical spending patterns,
                        seasonal trends, and market indicators to predict future expenses. While these predictions are based on sophisticated
                        analysis, they should be used as guidance rather than guaranteed outcomes.
                    </p>
                    <hr />
                    <p className="mb-0 small">
                        <i className="ri-error-warning-line me-1"></i>
                        Disclaimer: Forecasts are estimates based on historical data and patterns. Actual expenses may vary due to
                        unforeseen circumstances, market conditions, or changes in spending behavior. Always maintain a financial buffer
                        and regularly review your budget.
                    </p>
                </Alert>

                {error === 'No historical transaction data available' ? (
                    <Alert variant="warning" className="mb-4">
                        <Alert.Heading>No Transaction Data Found</Alert.Heading>
                        <p>To use the AI forecasting feature, you need to have historical expense data. You can add your actual transactions through the Transactions page.</p>
                    </Alert>
                ) : error && (
                    <Alert variant="danger" className="mb-4">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                    </Alert>
                )}

                <Row className="g-4">
                    <Col md={3}>
                        <Card>
                            <Card.Body>
                                <h6 className="mb-4">Forecast Settings</h6>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Timeframe</Form.Label>
                                        <Form.Select 
                                            value={selectedTimeframe}
                                            onChange={(e) => setSelectedTimeframe(e.target.value)}
                                        >
                                            <option value="3months">3 Months</option>
                                            <option value="6months">6 Months</option>
                                            <option value="12months">12 Months</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Confidence Level</Form.Label>
                                        <Form.Select
                                            value={confidenceLevel}
                                            onChange={(e) => setConfidenceLevel(e.target.value)}
                                        >
                                            <option value="conservative">Conservative (90%)</option>
                                            <option value="medium">Medium (80%)</option>
                                            <option value="aggressive">Aggressive (70%)</option>
                                        </Form.Select>
                                    </Form.Group>
                                    <Button 
                                        variant="primary" 
                                        className="w-100"
                                        onClick={() => fetchData()}
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Update Forecast'}
                                    </Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={9}>
                        <Card className="mb-4">
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Generating forecast...</p>
                                    </div>
                                ) : (
                                    <div style={{ height: '400px' }}>
                                        {forecastData ? (
                                            <Line options={chartOptions} data={getChartData()} />
                                        ) : (
                                            <div className="text-center p-4">
                                                <p>No forecast data available</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                        {renderInsightsCard()}
                        {forecastData && (
                            <Card>
                                <Card.Header>
                                    <Card.Title as="h6">
                                        <i className="ri-file-list-3-line me-2"></i>
                                        Expense Forecast Details
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <h6 className="text-muted mb-1">Average Monthly Expense</h6>
                                                <h3>${Math.round(forecastData.predicted.reduce((a, b) => a + b, 0) / forecastData.predicted.length)}</h3>
                                                <small className={forecastData.trend[forecastData.trend.length - 1] > forecastData.trend[0] ? 'text-danger' : 'text-success'}>
                                                    <i className={`ri-arrow-${forecastData.trend[forecastData.trend.length - 1] > forecastData.trend[0] ? 'up' : 'down'}-line`}></i> 
                                                    {((forecastData.trend[forecastData.trend.length - 1] / forecastData.trend[0] - 1) * 100).toFixed(1)}% trend
                                                </small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <h6 className="text-muted mb-1">Forecast Range</h6>
                                                <h3>${Math.round(forecastData.lower_bound[forecastData.lower_bound.length - 1])} - ${Math.round(forecastData.upper_bound[forecastData.upper_bound.length - 1])}</h3>
                                                <small className="text-muted">End of forecast period</small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <h6 className="text-muted mb-1">Confidence Level</h6>
                                                <h3>{confidenceLevel === 'conservative' ? '90%' : confidenceLevel === 'medium' ? '80%' : '70%'}</h3>
                                                <small className="text-muted">Prediction interval</small>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </div>
        </React.Fragment>
    );
} 